"use client"

import { useState } from "react"
import { motion } from "framer-motion"
import Header from "@/components/header"
import Footer from "@/components/footer"
import { StarfieldBackground } from "@/components/starfield-background"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import {
    UserCheck,
    Shield,
    Clock,
    CheckCircle2,
    FileText,
    BadgeCheck,
    ArrowRight,
    Zap,
    Globe,
    Lock,
    Camera,
    CreditCard,
    MapPin,
    ChevronDown,
    Star,
    Quote
} from "lucide-react"
import Link from "next/link"
import { cn } from "@/lib/utils"

const features = [
    {
        icon: FileText,
        title: "Document Verification",
        description: "Government-issued ID verification including passport, driver's license, or national ID card"
    },
    {
        icon: Camera,
        title: "Live Selfie Check",
        description: "Real-time facial recognition matching to prevent identity fraud"
    },
    {
        icon: MapPin,
        title: "Address Verification",
        description: "Proof of address through utility bills, bank statements, or official documents"
    },
    {
        icon: Shield,
        title: "Background Screening",
        description: "Comprehensive background checks to ensure project legitimacy"
    }
]

const benefits = [
    "KYC Verified badge displayed on your presale page",
    "Increased investor trust and participation",
    "Priority listing on TokenKickstarter",
    "Access to higher fundraising limits",
    "Featured placement in presale listings",
    "Verified team member profiles"
]

const process = [
    { step: 1, title: "Submit Application", description: "Fill out the KYC application form with project details" },
    { step: 2, title: "Document Upload", description: "Upload required identity and address documents" },
    { step: 3, title: "Verification", description: "Our team reviews and verifies your documents" },
    { step: 4, title: "Approval", description: "Receive your KYC badge within 24-48 hours" }
]

const pricing = [
    {
        name: "Individual KYC",
        price: "$99",
        description: "For single project founders",
        features: [
            "1 team member verification",
            "KYC Verified badge",
            "24-48 hour processing",
            "Valid for 1 year"
        ]
    },
    {
        name: "Team KYC",
        price: "$249",
        description: "For project teams",
        features: [
            "Up to 5 team members",
            "KYC Verified badge",
            "Priority processing",
            "Valid for 1 year",
            "Team profile page"
        ],
        popular: true
    },
    {
        name: "Enterprise KYC",
        price: "$499",
        description: "For registered companies",
        features: [
            "Unlimited team members",
            "Company verification",
            "Express 12-hour processing",
            "Valid for 2 years",
            "Dedicated support"
        ]
    }
]

const faqs = [
    {
        question: "What documents do I need for KYC verification?",
        answer: "You'll need a valid government-issued ID (passport, driver's license, or national ID), a recent selfie holding your ID, and proof of address dated within the last 3 months (utility bill, bank statement, or official document)."
    },
    {
        question: "How long does the verification process take?",
        answer: "Standard verification takes 24-48 hours. Team KYC has priority processing, and Enterprise KYC offers express 12-hour processing. Complex cases may require additional time."
    },
    {
        question: "Is my personal information secure?",
        answer: "Absolutely. All documents are encrypted using AES-256 encryption and stored securely. We comply with GDPR and other privacy regulations. Your data is never shared with third parties."
    },
    {
        question: "Can I update my KYC information later?",
        answer: "Yes, you can update your information at any time. If you need to add team members or update documents, simply submit a new application referencing your existing verification."
    },
    {
        question: "What happens if my KYC application is rejected?",
        answer: "If rejected, you'll receive detailed feedback on why and how to resolve the issues. Most rejections are due to unclear documents or mismatched information. You can resubmit after addressing the concerns."
    },
    {
        question: "Do I need to renew my KYC verification?",
        answer: "Individual and Team KYC are valid for 1 year, while Enterprise KYC is valid for 2 years. You'll receive reminders before expiration to renew your verification."
    }
]

const testimonials = [
    {
        name: "Alex Chen",
        project: "DeFi Protocol",
        image: null,
        quote: "Getting KYC verified boosted our presale participation by 280%. Investors felt confident knowing our team was verified.",
        raised: "$2.4M"
    },
    {
        name: "Sarah Johnson",
        project: "GameFi Studio",
        image: null,
        quote: "The verification process was quick and professional. The KYC badge on our presale page made a huge difference in building trust.",
        raised: "$1.8M"
    },
    {
        name: "Michael Torres",
        project: "NFT Marketplace",
        image: null,
        quote: "Enterprise KYC was perfect for our team. The 12-hour processing meant we could launch on schedule. Highly recommended!",
        raised: "$3.2M"
    }
]

// Animation variants
const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" as const } }
}

const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1,
            delayChildren: 0.2
        }
    }
}

const staggerItem = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" as const } }
}

const scaleIn = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: "easeOut" as const } }
}

export function KYCContent() {
    const [openFaq, setOpenFaq] = useState<number | null>(null)

    return (
        <div className="min-h-screen flex flex-col relative">
            <StarfieldBackground />
            <Header />
            <main className="flex-1 relative z-10">
                {/* Hero Section */}
                <section className="py-20 md:py-28">
                    <div className="container">
                        <motion.div
                            className="max-w-4xl mx-auto text-center"
                            initial="hidden"
                            animate="visible"
                            variants={staggerContainer}
                        >
                            <motion.div
                                variants={staggerItem}
                                className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#7ed957]/30 bg-[#7ed957]/10 px-4 py-1.5 text-sm font-medium text-[#7ed957]"
                            >
                                <UserCheck className="h-4 w-4" />
                                KYC Verification Service
                            </motion.div>
                            <motion.h1
                                variants={staggerItem}
                                className="mb-6 text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl"
                            >
                                Build Trust with{" "}
                                <span className="bg-gradient-to-r from-[#7ed957] to-[#7ed957] bg-clip-text text-transparent">
                                    KYC Verification
                                </span>
                            </motion.h1>
                            <motion.p
                                variants={staggerItem}
                                className="mb-8 text-xl text-muted-foreground max-w-2xl mx-auto"
                            >
                                Verify your identity and build investor confidence.
                                KYC-verified projects see up to 3x higher participation rates.
                            </motion.p>
                            <motion.div
                                variants={staggerItem}
                                className="flex flex-col sm:flex-row gap-4 justify-center"
                            >
                                <Button
                                    size="lg"
                                    className="bg-[#7ed957] hover:bg-[#6bc448]"
                                    asChild
                                >
                                    <Link href="/kyc/apply">
                                        Apply for KYC
                                        <ArrowRight className="ml-2 h-5 w-5" />
                                    </Link>
                                </Button>
                                <Button size="lg" variant="outline" asChild>
                                    <a href="#pricing">View Pricing</a>
                                </Button>
                            </motion.div>
                        </motion.div>
                    </div>
                </section>

                {/* What We Verify */}
                <section className="py-20 bg-muted/30">
                    <div className="container">
                        <motion.div
                            className="text-center mb-16"
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: "-100px" }}
                            variants={fadeInUp}
                        >
                            <h2 className="text-3xl font-bold mb-4">What We Verify</h2>
                            <p className="text-muted-foreground max-w-2xl mx-auto">
                                Our comprehensive verification process ensures project founders are who they claim to be
                            </p>
                        </motion.div>
                        <motion.div
                            className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4"
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: "-100px" }}
                            variants={staggerContainer}
                        >
                            {features.map((feature) => {
                                const Icon = feature.icon
                                return (
                                    <motion.div key={feature.title} variants={staggerItem}>
                                        <Card className="group border-border/40 hover:border-[#7ed957]/50 transition-all h-full hover:shadow-lg hover:shadow-[#7ed957]/10">
                                            <CardContent className="p-6">
                                                <div className="mb-4">
                                                    <motion.div
                                                        className="h-14 w-14 flex items-center justify-center rounded-2xl bg-[#7ed957]/10 group-hover:bg-[#7ed957]/20 transition-all"
                                                        whileHover={{ scale: 1.1, rotate: 5 }}
                                                    >
                                                        <Icon className="h-7 w-7 text-[#7ed957]" />
                                                    </motion.div>
                                                </div>
                                                <h3 className="font-bold mb-2">{feature.title}</h3>
                                                <p className="text-sm text-muted-foreground">{feature.description}</p>
                                            </CardContent>
                                        </Card>
                                    </motion.div>
                                )
                            })}
                        </motion.div>
                    </div>
                </section>

                {/* Process */}
                <section className="py-20">
                    <div className="container">
                        <motion.div
                            className="text-center mb-16"
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: "-100px" }}
                            variants={fadeInUp}
                        >
                            <h2 className="text-3xl font-bold mb-4">Simple Verification Process</h2>
                            <p className="text-muted-foreground">Get verified in 4 easy steps</p>
                        </motion.div>
                        <div className="max-w-4xl mx-auto">
                            <motion.div
                                className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4"
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, margin: "-100px" }}
                                variants={staggerContainer}
                            >
                                {process.map((item, index) => (
                                    <motion.div
                                        key={item.step}
                                        className="relative"
                                        variants={staggerItem}
                                    >
                                        <motion.div
                                            className="text-center p-6 rounded-xl bg-muted/30 hover:bg-muted/50 transition-all"
                                            whileHover={{ y: -5 }}
                                        >
                                            <motion.div
                                                className="h-14 w-14 mx-auto flex items-center justify-center rounded-full bg-[#7ed957] text-white font-bold text-xl mb-4"
                                                initial={{ scale: 0 }}
                                                whileInView={{ scale: 1 }}
                                                viewport={{ once: true }}
                                                transition={{ delay: index * 0.15, type: "spring", stiffness: 300 }}
                                            >
                                                {item.step}
                                            </motion.div>
                                            <h3 className="font-bold mb-2">{item.title}</h3>
                                            <p className="text-sm text-muted-foreground">{item.description}</p>
                                        </motion.div>
                                    </motion.div>
                                ))}
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* Benefits */}
                <section className="py-20 bg-muted/30">
                    <div className="container">
                        <motion.div
                            className="grid lg:grid-cols-2 gap-12 items-center"
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: "-100px" }}
                            variants={staggerContainer}
                        >
                            <motion.div variants={staggerItem}>
                                <h2 className="text-3xl font-bold mb-6">Benefits of KYC Verification</h2>
                                <p className="text-muted-foreground mb-8">
                                    Stand out from the crowd and show investors you're serious about transparency and security.
                                </p>
                                <ul className="space-y-4">
                                    {benefits.map((benefit, index) => (
                                        <motion.li
                                            key={index}
                                            className="flex items-center gap-3"
                                            initial={{ opacity: 0, x: -20 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ delay: index * 0.1 }}
                                        >
                                            <CheckCircle2 className="h-5 w-5 text-[#7ed957] shrink-0" />
                                            <span>{benefit}</span>
                                        </motion.li>
                                    ))}
                                </ul>
                            </motion.div>
                            <motion.div
                                className="glass-card p-8"
                                variants={scaleIn}
                            >
                                <div className="flex items-center gap-4 mb-6">
                                    <motion.div
                                        className="h-16 w-16 rounded-full bg-[#7ed957]/10 flex items-center justify-center"
                                        animate={{ rotate: [0, 10, -10, 0] }}
                                        transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                                    >
                                        <BadgeCheck className="h-8 w-8 text-[#7ed957]" />
                                    </motion.div>
                                    <div>
                                        <div className="text-2xl font-bold">KYC Verified</div>
                                        <div className="text-muted-foreground">Trusted Project Badge</div>
                                    </div>
                                </div>
                                <p className="text-muted-foreground mb-6">
                                    This badge is displayed prominently on your presale page, giving investors
                                    immediate confidence in your project's legitimacy.
                                </p>
                                <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-[#7ed957]/10 border border-[#7ed957]/20">
                                    <UserCheck className="h-5 w-5 text-[#7ed957]" />
                                    <span className="font-semibold text-[#7ed957]">KYC Verified</span>
                                    <span className="text-xs text-muted-foreground ml-auto">Verified Jan 2026</span>
                                </div>
                            </motion.div>
                        </motion.div>
                    </div>
                </section>

                {/* Testimonials */}
                <section className="py-20">
                    <div className="container">
                        <motion.div
                            className="text-center mb-16"
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: "-100px" }}
                            variants={fadeInUp}
                        >
                            <h2 className="text-3xl font-bold mb-4">Success Stories</h2>
                            <p className="text-muted-foreground max-w-2xl mx-auto">
                                See how KYC verification helped these projects build trust and raise more
                            </p>
                        </motion.div>
                        <motion.div
                            className="grid gap-8 md:grid-cols-3"
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: "-100px" }}
                            variants={staggerContainer}
                        >
                            {testimonials.map((testimonial, index) => (
                                <motion.div key={index} variants={staggerItem}>
                                    <Card className="h-full border-border/40 hover:border-[#7ed957]/30 transition-all">
                                        <CardContent className="p-6">
                                            <div className="flex items-center gap-1 mb-4">
                                                {[...Array(5)].map((_, i) => (
                                                    <Star key={i} className="h-4 w-4 fill-[#7ed957] text-[#7ed957]" />
                                                ))}
                                            </div>
                                            <Quote className="h-8 w-8 text-[#7ed957]/20 mb-2" />
                                            <p className="text-muted-foreground mb-6 italic">
                                                "{testimonial.quote}"
                                            </p>
                                            <div className="flex items-center justify-between border-t border-border pt-4">
                                                <div>
                                                    <div className="font-semibold">{testimonial.name}</div>
                                                    <div className="text-sm text-muted-foreground">{testimonial.project}</div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-sm text-muted-foreground">Raised</div>
                                                    <div className="font-bold text-[#7ed957]">{testimonial.raised}</div>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </section>

                {/* Pricing */}
                <section className="py-20 bg-muted/30" id="pricing">
                    <div className="container">
                        <motion.div
                            className="text-center mb-16"
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: "-100px" }}
                            variants={fadeInUp}
                        >
                            <h2 className="text-3xl font-bold mb-4">Simple, Transparent Pricing</h2>
                            <p className="text-muted-foreground">Choose the plan that fits your needs</p>
                        </motion.div>
                        <motion.div
                            className="grid gap-8 sm:grid-cols-2 md:grid-cols-3 max-w-5xl mx-auto"
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: "-100px" }}
                            variants={staggerContainer}
                        >
                            {pricing.map((plan, index) => (
                                <motion.div
                                    key={plan.name}
                                    variants={staggerItem}
                                    whileHover={{ y: -10 }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                >
                                    <Card
                                        className={cn(
                                            "relative h-full",
                                            plan.popular
                                                ? "border-[#7ed957] shadow-lg shadow-[#7ed957]/20"
                                                : "border-border/40"
                                        )}
                                    >
                                        {plan.popular && (
                                            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                                <span className="bg-[#7ed957] text-white text-xs font-bold px-3 py-1 rounded-full">
                                                    Most Popular
                                                </span>
                                            </div>
                                        )}
                                        <CardContent className="p-6">
                                            <div className="text-center mb-6">
                                                <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                                                <div className="text-4xl font-bold text-[#7ed957] mb-2">{plan.price}</div>
                                                <p className="text-sm text-muted-foreground">{plan.description}</p>
                                            </div>
                                            <ul className="space-y-3 mb-6">
                                                {plan.features.map((feature, idx) => (
                                                    <li key={idx} className="flex items-center gap-2 text-sm">
                                                        <CheckCircle2 className="h-4 w-4 text-[#7ed957] shrink-0" />
                                                        <span>{feature}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                            <Button
                                                className={cn(
                                                    "w-full",
                                                    plan.popular ? "bg-[#7ed957] hover:bg-[#6bc448]" : ""
                                                )}
                                                variant={plan.popular ? "default" : "outline"}
                                                asChild
                                            >
                                                <Link href="/kyc/apply">Get Started</Link>
                                            </Button>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </section>

                {/* FAQ */}
                <section className="py-20">
                    <div className="container">
                        <motion.div
                            className="text-center mb-16"
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: "-100px" }}
                            variants={fadeInUp}
                        >
                            <h2 className="text-3xl font-bold mb-4">Frequently Asked Questions</h2>
                            <p className="text-muted-foreground max-w-2xl mx-auto">
                                Everything you need to know about KYC verification
                            </p>
                        </motion.div>
                        <motion.div
                            className="max-w-3xl mx-auto space-y-4"
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: "-100px" }}
                            variants={staggerContainer}
                        >
                            {faqs.map((faq, index) => (
                                <motion.div
                                    key={index}
                                    variants={staggerItem}
                                    className="border border-border/40 rounded-xl overflow-hidden"
                                >
                                    <button
                                        onClick={() => setOpenFaq(openFaq === index ? null : index)}
                                        className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
                                    >
                                        <span className="font-medium">{faq.question}</span>
                                        <ChevronDown
                                            className={cn(
                                                "h-5 w-5 text-muted-foreground transition-transform",
                                                openFaq === index && "rotate-180"
                                            )}
                                        />
                                    </button>
                                    <motion.div
                                        initial={false}
                                        animate={{
                                            height: openFaq === index ? "auto" : 0,
                                            opacity: openFaq === index ? 1 : 0
                                        }}
                                        transition={{ duration: 0.3 }}
                                        className="overflow-hidden"
                                    >
                                        <p className="px-4 pb-4 text-muted-foreground">
                                            {faq.answer}
                                        </p>
                                    </motion.div>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </section>

                {/* CTA */}
                <section className="py-20 bg-muted/30">
                    <div className="container">
                        <motion.div
                            className="max-w-3xl mx-auto text-center"
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: "-100px" }}
                            variants={fadeInUp}
                        >
                            <h2 className="text-3xl font-bold mb-4">Ready to Get Verified?</h2>
                            <p className="text-muted-foreground mb-8">
                                Join hundreds of verified projects on TokenKickstarter and build investor trust today.
                            </p>
                            <motion.div
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <Button
                                    size="lg"
                                    className="bg-[#7ed957] hover:bg-[#6bc448]"
                                    asChild
                                >
                                    <Link href="/kyc/apply">
                                        Apply for KYC Now
                                        <ArrowRight className="ml-2 h-5 w-5" />
                                    </Link>
                                </Button>
                            </motion.div>
                        </motion.div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    )
}
